/* eslint-disable react/no-array-index-key */
/**
 * @fileoverview Componente de sugerencias de respuestas rápidas (FastAnswers) filtradas por término de búsqueda.
 * Muestra una lista de respuestas rápidas y permite seleccionar una para insertarla en el chat.
 * Cumple Atomic Design y separación estricta de UI Layer.
 *
 * @module FastAnswersShortcutMC
 * @category Component - Molecule
 * @since 2.0.0
 * @version 3.2.0 - JSDoc completo, integración con sharingInformationService
 *
 * @typedef {Object} FastAnswersShortcutMCProps
 * @property {string} searchTerm - Término de búsqueda para filtrar respuestas rápidas
 * @property {string|number} customer - ID del cliente activo
 * @property {string} platform - Plataforma de la conversación (ej: 'whatsapp')
 *
 * @component FastAnswersShortcutMC
 * @category Component - Molecule
 *
 * @description
 * **Características:**
 * - Lista de respuestas rápidas filtradas por término de búsqueda
 * - Permite seleccionar una respuesta para insertarla en el chat
 * - Integración con sharingInformationService para comunicar la selección
 * - Cumple Atomic Design: Molécula, reutilizable en cualquier flujo de chat
 * - Usa FastAnswerMC para renderizar cada respuesta
 *
 * **Atomic Design:**
 * - **Nivel:** Molecule
 * - **Composición:** Usa FastAnswerMC
 * - **Dependencias:** useFastAnswers, sharingInformationService
 *
 * **Props Interface:**
 * ```javascript
 * {
 *   searchTerm: string,        // Término de búsqueda
 *   customer: string|number,  // ID del cliente
 *   platform: string          // Plataforma
 * }
 * ```
 *
 * @param {FastAnswersShortcutMCProps} props - Propiedades del componente
 * @returns {JSX.Element} Lista de respuestas rápidas renderizada
 *
 * @example
 * <FastAnswersShortcutMC searchTerm="hola" customer={123} platform="whatsapp" />
 *
 * @see {@link ./FastAnswerMC.js} Componente de respuesta individual
 * @see {@link ./hooks/useFastAnswers.js} Hook de datos
 * @see {@link ../../../services/sharingInformationService.js} Servicio de comunicación
 *
 * @since 2.0.0
 * @version 3.2.0 - JSDoc completo, integración con sharingInformationService
 */
import React, { memo, useCallback } from "react";
import { isEmpty } from "lodash";
import FastAnswerMC from "./FastAnswerMC";
import useFastAnswers from "./hooks/useFastAnswers";
import { sharingInformationService } from "../../../services/sharingInformationService";

const FastAnswersShortcutMC = memo(({
  searchTerm,
  customer,
  platform
}) => {
  const { fastAnswers } = useFastAnswers({
    searchTerm,
    customer,
    platform
  });

  const handleSelectAnswer = useCallback((answer, actionType) => {
    sharingInformationService.setSubject({
      actionType,
      onSelectAnswer: true,
      answer
    });
    sharingInformationService.setSubject({});
  }, []);

  return (
    <div className="tw-wrapper tw-bg-blue-btn tw-w-full tw-p-[10px] tw-rounded-[25px] tw-shadow-[0_0_5px_0_black] tw-bg-[#EBF7FC] tw-mb-16">
      <div className="tw-max-h-[200px] tw-overflow-y-auto tw-pl-[10px] tw-pr-[10px] tw-mt-[5px]">
        {fastAnswers?.map((answer, index) => (
          <div key={`fast-answer-${index}`} className={index > 0 ? "tw-mt-[10px]" : ""}>
            <FastAnswerMC
              answer={answer}
              displayTeam
              onSelectAnswer={handleSelectAnswer}
            />
          </div>
        ))}
        {isEmpty(fastAnswers) && (
          <div className="tw-text-center tw-py-4 tw-text-gray-500">
            No se encontraron respuestas rápidas
          </div>
        )}
      </div>
    </div>
  );
});

FastAnswersShortcutMC.displayName = "FastAnswersShortcutMC";

export default FastAnswersShortcutMC;
